import React, { useEffect, useState } from "react";
import { X, Download, QrCode as QrIcon } from "lucide-react";
import { API, authedFetch } from "../lib/api";

/**
 * Overlay dialog that fetches a QR code PNG from the backend
 * and displays it full-size with a download button.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - kind: 'dpi' | 'scaffold'
 *  - id: string  (device_id or scaffold_id)
 *  - title: string
 *  - filename: string  (download filename, without extension)
 */
export default function QrOverlay({ open, onClose, kind, id, title, filename }) {
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setUrl(null);
    setBlob(null);
    setError("");
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      try {
        const endpoint = kind === "dpi" ? `/devices/${id}/qrcode` : `/scaffolds/${id}/qrcode`;
        const res = await authedFetch(`${API}${endpoint}`);
        if (!res.ok) {
          setError("Errore caricamento QR");
          return;
        }
        const b = await res.blob();
        if (cancelled) return;
        const u = URL.createObjectURL(b);
        currentUrl = u;
        setBlob(b);
        setUrl(u);
      } catch {
        if (!cancelled) setError("Errore caricamento QR");
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) { try { URL.revokeObjectURL(currentUrl); } catch { /* ignore */ } }
    };
  }, [open, kind, id]);

  if (!open) return null;

  const download = () => {
    if (!blob) return;
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `${(filename || "qr").replace(/\s+/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 0);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="qr-overlay"
    >
      <div
        className="bru-card w-full max-w-md p-6 bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="tag-label flex items-center gap-1"><QrIcon className="w-3 h-3" /> QR CODE</div>
            <h3 className="text-2xl font-bold tracking-tight" >
              {title || "QR Code"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="bru-btn bru-btn-ghost !px-2 !py-1"
            data-testid="close-qr-overlay"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-4">
          <div className="border-[1.5px] border-black bg-white p-4 rounded-xl bru-shadow-sm">
            {url ? (
              <img src={url} alt="QR Code" className="w-64 h-64 object-contain" data-testid="qr-image" />
            ) : error ? (
              <div className="w-64 h-64 flex items-center justify-center text-[#ff3b30] text-sm">
                {error}
              </div>
            ) : (
              <div className="w-64 h-64 animate-pulse bg-neutral-100" />
            )}
          </div>
          <p className="text-xs text-neutral-500 text-center max-w-xs">
            Scansiona o stampa il QR per visualizzare lo stato in tempo reale.
          </p>
          <button
            onClick={download}
            disabled={!blob}
            className="bru-btn bru-btn-primary w-full disabled:opacity-50"
            data-testid="download-qr-button"
          >
            <Download className="w-4 h-4" />
            Scarica PNG
          </button>
        </div>
      </div>
    </div>
  );
}
